import { useAction, useAtom } from '@reatom/npm-react'
import { motion } from 'motion/react'
import styled from 'styled-components'

import { Array, AsyncStateN, pipe } from '@blade/std-lib'
import {
  trendingPairsModelAtom,
  trendingPairsStateAtom,
} from '@root/features/trending-pairs'
import { FadeOverflow, hiddenScrollbarCss, Marquee } from '@root/shared/ui-kit'

import { topbarPausedAtom } from '../atoms'
import { TopbarError } from '../common/topbar-error'
import { TopbarPairItemSkeleton } from '../common/topbar-pair-item-skeleton'
import { TOPBAR_POSITIONS_VARIANTS } from '../constants'

import { TopbarTrendingItem } from './topbar-trending-item'

type TopbarTrendingProps = {
  variant: 'interactive' | 'static'
}

export const TopbarTrending: React.FC<TopbarTrendingProps> = ({ variant }) => {
  const [model] = useAtom(trendingPairsModelAtom)
  const [trendingPairsState] = useAtom(trendingPairsStateAtom)
  const [pauseTopbarScroll] = useAtom(topbarPausedAtom)
  const load = useAction(model.execute)

  const handleReload = () => load()

  const renderMarquee = () =>
    AsyncStateN.matchWaiting(trendingPairsState, {
      Success: (trendingPairs) => {
        if (trendingPairs.length === 0) {
          return <TopbarError onReload={handleReload} intent="empty" />
        }

        return (
          <Marquee variant={variant} paused={pauseTopbarScroll}>
            {pipe(
              trendingPairs,
              Array.map((pair) => <Item key={pair.address} pair={pair} />),
            )}
          </Marquee>
        )
      },
      Error: () => <TopbarError onReload={handleReload} />,
      Waiting: () => (
        <List>
          <TopbarPairItemSkeleton />
          <TopbarPairItemSkeleton />
          <TopbarPairItemSkeleton />
        </List>
      ),
    })

  return (
    <Root
      variants={TOPBAR_POSITIONS_VARIANTS}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <ListContainer
        inlineStart={12}
        inlineEnd={12}
        mode="always"
        variant="horizontal"
      >
        {renderMarquee()}
      </ListContainer>
    </Root>
  )
}

const Root = styled(motion.div)`
  padding-inline: 0 0;
  display: grid;
  grid-template-columns: auto [list] minmax(0, 1fr);
  min-width: 0;
`

const ListContainer = styled(FadeOverflow)`
  min-width: 0;
  overflow-x: hidden;
  grid-column: list;

  ${hiddenScrollbarCss}
`

const List = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  margin-inline: 12px;
  width: min-content;
`

const Item = styled(TopbarTrendingItem)`
  padding-inline: 5px;
`
